import { useRef, useState, useEffect } from 'react';
import { categories } from '@/mocks/categories';

const categoryLabels: Record<string, string> = {
  'Shoes': 'Footwear',
  'Hoodies/Sweaters': 'Knitwear',
  'T-Shirts': 'Essentials',
  'Jackets': 'Outerwear',
  'Pants/Shorts': 'Bottoms',
  'Headwear': 'Caps',
  'Sets': 'Outfits',
  'Underwear/Underpants': 'Basics',
  'Jersey': 'Sports',
  'Accessories': 'Extras',
  'Bags': 'Carry',
  'Belts': 'Waist',
  'Perfumes': 'Scents',
  'OtherStuff': 'More',
};

const categoryIcons: Record<string, string> = {
  'Shoes': 'ri-footprint-line',
  'Hoodies/Sweaters': 'ri-t-shirt-line',
  'T-Shirts': 'ri-shirt-line',
  'Jackets': 'ri-cloudy-line',
  'Pants/Shorts': 'ri-check-line',
  'Headwear': 'ri-user-smile-line',
  'Sets': 'ri-stack-line',
  'Underwear/Underpants': 'ri-checkbox-blank-circle-line',
  'Jersey': 'ri-basketball-line',
  'Accessories': 'ri-glasses-line',
  'Bags': 'ri-handbag-line',
  'Belts': 'ri-loop-left-line',
  'Perfumes': 'ri-drop-line',
  'OtherStuff': 'ri-more-line',
};

export default function CategoryGrid() {
  const scrollRef = useRef<HTMLDivElement>(null);
  const [canScrollLeft, setCanScrollLeft] = useState(false);
  const [canScrollRight, setCanScrollRight] = useState(true);

  const checkScroll = () => {
    const el = scrollRef.current;
    if (!el) return;
    setCanScrollLeft(el.scrollLeft > 10);
    setCanScrollRight(el.scrollLeft + el.clientWidth < el.scrollWidth - 10);
  };

  useEffect(() => {
    const el = scrollRef.current;
    if (!el) return;
    checkScroll();
    el.addEventListener('scroll', checkScroll);
    return () => el.removeEventListener('scroll', checkScroll);
  }, []);

  const scroll = (direction: 'left' | 'right') => {
    const el = scrollRef.current;
    if (!el) return;
    el.scrollBy({ left: direction === 'left' ? -340 : 340, behavior: 'smooth' });
  };

  return (
    <section className="bg-foreground-950 py-16 md:py-24">
      <div className="px-4 md:px-6">
        <div className="flex flex-col lg:flex-row lg:items-end gap-6 mb-12">
          <span className="inline-flex items-center gap-2 px-5 py-2.5 rounded-full border border-background-50/20 text-background-50/70 text-sm font-medium whitespace-nowrap">
            <i className="ri-grid-line"></i>
            All Categories
          </span>
          <h2 className="font-heading text-3xl md:text-5xl lg:text-6xl font-bold text-background-50 tracking-tight flex-1">
            Browse by Category
          </h2>
          <div className="flex items-center gap-3">
            <button
              onClick={() => scroll('left')}
              disabled={!canScrollLeft}
              className="w-10 h-10 rounded-full border border-background-50/20 flex items-center justify-center text-background-50/50 hover:text-background-50 hover:border-background-50/40 transition-all disabled:opacity-30 disabled:cursor-not-allowed cursor-pointer"
            >
              <i className="ri-arrow-left-s-line text-lg"></i>
            </button>
            <button
              onClick={() => scroll('right')}
              disabled={!canScrollRight}
              className="w-10 h-10 rounded-full bg-background-50 flex items-center justify-center text-foreground-950 hover:bg-background-50/90 transition-all disabled:opacity-30 disabled:cursor-not-allowed cursor-pointer"
            >
              <i className="ri-arrow-right-s-line text-lg"></i>
            </button>
          </div>
        </div>
      </div>

      <div
        ref={scrollRef}
        className="flex gap-4 overflow-x-auto px-4 md:px-6 pb-4"
        style={{ scrollbarWidth: 'none', msOverflowStyle: 'none' }}
      >
        {categories.map((cat) => (
          <a
            key={cat.name}
            href={cat.url}
            target="_blank"
            rel="noopener nofollow"
            className="flex-shrink-0 w-52 md:w-60 group cursor-pointer"
          >
            <div className="bg-background-50 rounded-xl overflow-hidden transition-transform duration-300 group-hover:-translate-y-1">
              <div className="relative w-full h-44 md:h-48 overflow-hidden">
                <img
                  src={cat.image}
                  alt={cat.name}
                  className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-105"
                />
                <div className="absolute top-3 left-3">
                  <span className="inline-flex items-center gap-1 px-2 py-1 rounded-md bg-black/40 text-white text-xs font-medium">
                    <i className={`${categoryIcons[cat.name] || 'ri-price-tag-3-line'} text-xs`}></i>
                    {categoryLabels[cat.name] || cat.name}
                  </span>
                </div>
              </div>
              <div className="p-4">
                <p className="font-heading font-semibold text-foreground-900 text-sm md:text-base">{cat.name}</p>
                <p className="text-xs text-foreground-500 mt-1 leading-relaxed">{cat.description}</p>
              </div>
            </div>
          </a>
        ))}
      </div>
    </section>
  );
}